// Phase 1: Lightweight drop target that accepts files, fingerprints them,
// enqueues jobs, and dismisses. All review happens in the persistent Ingest Queue.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { uploadFiles } from './ingestApi';
import ProcessingOverlay from './ProcessingOverlay';
import SampleOverlay from './SampleOverlay';
import { SAMPLE_DOCS, SAMPLE_ORDER } from './sampleDocs';

interface FileResult {
  status: string;
  filename: string;
  jobId?: string;
  detail?: string;
}

interface UploadResult {
  ok: boolean;
  total: number;
  queued: number;
  duplicates: number;
  errors: number;
  results: FileResult[];
}

interface CaptureModalProps {
  onClose: () => void;
}

const extOf = (filename: string) => {
  const dot = String(filename || '').lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toUpperCase().slice(0, 4) : 'FILE';
};

const sizeOf = (bytes: number) => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
};

const ACCEPTED = [
  { ext: '.pdf', label: 'PDF' },
  { ext: '.doc', label: null },
  { ext: '.docx', label: 'DOCX' },
  { ext: '.xls', label: null },
  { ext: '.xlsx', label: 'XLSX' },
  { ext: '.xml', label: null },
  { ext: '.png', label: 'images' },
  { ext: '.jpg', label: null },
  { ext: '.jpeg', label: null },
  { ext: '.tiff', label: null },
  { ext: '.txt', label: 'TXT' },
  { ext: '.csv', label: 'CSV' },
];
const ACCEPT_ATTR = ACCEPTED.map((a) => a.ext).filter((e) => e !== '.xml').join(',');
const ACCEPT_HINT = `${ACCEPTED.map((a) => a.label).filter(Boolean).join(' · ')} — up to 50MB each`;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function CaptureModal({ onClose }: CaptureModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<Set<number>>(() => new Set());
  const [viewProcessing, setViewProcessing] = useState(false);
  const [sampleKey, setSampleKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const node = modalRef.current;
    const first = node?.querySelector(FOCUSABLE);
    (first || node)?.focus();
    return () => { previouslyFocused.current?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (sampleKey || result) return;
        if (!uploading) onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = modalRef.current;
      if (!node) return;
      const els = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!els.length) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [uploading, onClose, sampleKey, result]);

  const guardedClose = useCallback(() => {
    if (uploading) return;
    onClose();
  }, [uploading, onClose]);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const unique = Array.from(newFiles).filter((f) => !existing.has(`${f.name}-${f.size}`));
      return [...prev, ...unique];
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (uploading) return;
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles, uploading]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    setDragging(true);
  }, [uploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  }, [addFiles]);

  const handleDropzoneKey = useCallback((e: React.KeyboardEvent) => {
    if (uploading) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }, [uploading]);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!files.length || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const res = await uploadFiles(files, 'modal') as unknown as UploadResult;
      setResult(res);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [files, uploading]);

  const handleDone = useCallback(() => {
    onClose();
  }, [onClose]);

  const retryFile = useCallback(async (index: number) => {
    const file = files[index];
    const expected = result?.results?.[index]?.filename;
    if (!file || retrying.has(index)) return;
    if (expected != null && file.name !== expected) return;
    setRetrying((prev) => new Set(prev).add(index));
    try {
      const res = await uploadFiles([file], 'modal') as unknown as UploadResult;
      const replacement: FileResult = (res.results || [])[0] || { filename: file.name, status: 'error', detail: 'Retry returned no result' };
      setResult((prev) => {
        if (!prev) return prev;
        const merged = prev.results.map((r, i) => (i === index ? { ...replacement, filename: r.filename } : r));
        return {
          ...prev,
          results: merged,
          queued: merged.filter((r) => r.status === 'queued').length,
          duplicates: merged.filter((r) => r.status === 'duplicate').length,
        };
      });
    } catch (err: any) {
      setResult((prev) => {
        if (!prev) return prev;
        return { ...prev, results: prev.results.map((r, i) => (i === index ? { ...r, status: 'error', detail: err.message } : r)) };
      });
    } finally {
      setRetrying((prev) => { const next = new Set(prev); next.delete(index); return next; });
    }
  }, [files, retrying, result]);

  const queuedJobs = (result?.results || []).filter((r) => r.status === 'queued' && r.jobId);
  const failedResults = (result?.results || [])
    .map((r, index) => ({ ...r, index }))
    .filter((r) => r.status === 'error');

  if (sampleKey) {
    return <SampleOverlay sample={SAMPLE_DOCS[sampleKey]} onClose={guardedClose} />;
  }

  if (result && queuedJobs.length > 0 && (failedResults.length === 0 || viewProcessing)) {
    return <ProcessingOverlay uploads={queuedJobs.map((r) => ({ jobId: r.jobId!, filename: r.filename }))} onClose={handleDone} />;
  }

  if (result) {
    const liveMessage = failedResults.length > 0
      ? `${result.queued} queued, ${failedResults.length} failed`
      : result.duplicates > 0 && result.queued === 0
        ? 'Nothing new to process — these documents are already on file.'
        : 'Upload complete.';
    return createPortal(
      <div className="ingest-modal-overlay bi-console" onClick={guardedClose}>
        <div
          className="ingest-modal"
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Upload complete"
        >
          <div className="ingest-modal-head ing-navy-head">
            <h2>Upload complete</h2>
            <button className="ingest-modal-x" onClick={guardedClose} aria-label="Close">✕</button>
          </div>
          <div className="ingest-modal-body">
            <div className="ingest-success-stats">
              <span>
                <strong className="queued-count">{result.queued}</strong> queued
              </span>
              {result.duplicates > 0 && (
                <span>
                  <strong className="duplicate-count">{result.duplicates}</strong> duplicate{result.duplicates !== 1 ? 's' : ''} skipped
                </span>
              )}
              {failedResults.length > 0 && (
                <span>
                  <strong className="ingest-failed-count">{failedResults.length}</strong> failed
                </span>
              )}
            </div>

            {failedResults.length === 0 ? (
              <div className="ingest-success-hint">
                {result.duplicates > 0 && result.queued === 0
                  ? 'Nothing new to process — these documents are already on file.'
                  : 'Review them in the Ingest Queue.'}
              </div>
            ) : (
              <div className="ingest-file-list ingest-failed-list">
                {failedResults.map((r) => (
                  <div key={r.index} className="ingest-file-item ingest-file-item-error">
                    <span className="ingest-file-ext">{extOf(r.filename)}</span>
                    <span className="ingest-file-name" title={r.filename}>{r.filename}</span>
                    <span className="ingest-file-error-detail">{r.detail || 'Upload failed'}</span>
                    <button
                      className="btn ingest-retry-btn"
                      onClick={() => retryFile(r.index)}
                      disabled={retrying.has(r.index) || files[r.index]?.name !== r.filename}
                      aria-label={`Retry ${r.filename}`}
                    >
                      {retrying.has(r.index) ? 'Retrying…' : 'Retry'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {failedResults.length > 0 && (
              <div className="pb-sr-only" aria-live="polite" aria-atomic="true">{liveMessage}</div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {queuedJobs.length > 0 && failedResults.length > 0 && !viewProcessing && (
                <button className="btn" onClick={() => setViewProcessing(true)}>
                  View progress
                </button>
              )}
              <button className="btn btn--primary" onClick={handleDone}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="ingest-modal-overlay bi-console" onClick={guardedClose}>
      <div
        className="ingest-modal"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Upload documents"
      >
        <div className="ingest-modal-head ing-navy-head">
          <h2>Upload documents</h2>
          <button className="ingest-modal-x" onClick={guardedClose} disabled={uploading} aria-label="Close">✕</button>
        </div>

        <div className="ingest-modal-body">
          <div
            className={`ingest-dropzone${dragging ? ' dragging' : ''}`}
            role="button"
            tabIndex={0}
            aria-disabled={uploading}
            aria-label="Upload documents. Drop files here, or press Enter to browse your computer."
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => { if (!uploading) inputRef.current?.click(); }}
            onKeyDown={handleDropzoneKey}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept={ACCEPT_ATTR}
              tabIndex={-1}
              aria-hidden="true"
            />
            <div className="ingest-dropzone-icon" aria-hidden="true">↑</div>
            <p className="ingest-dropzone-label">
              {dragging ? <b>Drop files here</b> : <><b>Drop files here</b> or click to browse</>}
            </p>
            <p className="ingest-dropzone-hint">
              {ACCEPT_HINT}
            </p>
            <p className="ingest-dropzone-note">
              XML files are blocked by network security in production — convert to PDF first.
            </p>
          </div>

          <div className="ing-demo-samples">
            <div className="ing-demo-label">
              <span className="eyebrow">Or open a sample</span>
              <span className="ing-demo-rule" />
            </div>
            {SAMPLE_ORDER.map((key) => {
              const d = SAMPLE_DOCS[key];
              if (!d) return null;
              return (
                <button
                  key={d.key}
                  type="button"
                  className="ing-demo-item"
                  onClick={() => setSampleKey(d.key)}
                  disabled={uploading}
                >
                  <span className="ing-demo-name">{d.file}</span>
                  <span className="ing-demo-desc">{d.desc}</span>
                  <span className="ing-demo-arrow" aria-hidden="true">→</span>
                </button>
              );
            })}
          </div>

          {files.length > 0 && (
            <div className="ingest-file-list">
              {files.map((file, i) => (
                <div key={`${file.name}-${file.size}-${i}`} className="ingest-file-item">
                  <span className="ingest-file-ext">{extOf(file.name)}</span>
                  <span className="ingest-file-name">{file.name}</span>
                  <span className="ingest-file-size">{sizeOf(file.size)}</span>
                  <button
                    className="ingest-file-remove"
                    onClick={() => removeFile(i)}
                    disabled={uploading}
                    title="Remove"
                    aria-label={`Remove ${file.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="ingest-error" role="alert">
              {error}
            </div>
          )}

          <div className="pb-sr-only" aria-live="polite" aria-atomic="true">
            {uploading ? `Uploading ${files.length} file${files.length === 1 ? '' : 's'}…` : ''}
          </div>

          <div className="ingest-actions">
            <div className="ingest-file-count">
              {files.length > 0 && `${files.length} file${files.length !== 1 ? 's' : ''} selected`}
            </div>
            <div className="ingest-buttons">
              <button className="btn" onClick={guardedClose} disabled={uploading}>
                Cancel
              </button>
              <button
                className="btn btn--primary ing-gold-btn"
                onClick={handleUpload}
                disabled={!files.length || uploading}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
